/**
 * 串联层：整个客户端**唯一**知道「大厅 / 牌桌 / 行动条分别由谁实现」的地方，
 * 也是唯一建连的地方（真正的建连细节在 `app/session.ts`）。
 *
 * 主路径是**联机**：
 *
 *   建连（auth → /api/config → WsTransport → 镜像 → connect）
 *     → 大厅（建房 / 输码进房；地址栏带码就自动进）
 *     → 房间等待室（坐下 / 开始）
 *     → 牌桌对局（ActionPrompt → ActionDock → playerAction）
 *     → 手牌结算卡 → 服务端自动开下一手
 *
 * 数据流全程单向、只有一个真源：
 *
 *   WsTransport → useNetStore.view (PlayerView)
 *               → roomMirror(buildTableVM) → tableStore.view → TableScene / useTableFx
 *               → ActionDockBinding（读 prompt）→ link.send(playerAction)
 *
 * 「一台手机轮流玩」的本地模式仍然保留，走另一套 store（`localSession`）。
 * 两者互斥：本地开局前会把联机镜像停掉（`leaveLocalGame` 里 reset 三个 store），
 * 所以 `tableStore.view` 任何时刻都只有一个写者。
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import type { ActionIntent } from '@poker/shared'

import { ActionDock } from './action/ActionDock.js'
import { App } from './App.js'
import { AudioMenu } from './fx/AudioSettings.js'
import { setPlaybackAudioSession } from './fx/audioContext.js'
import { useBackgroundMusic } from './fx/useBackgroundMusic.js'
import { leaveRoomAndReset, useOnlineBoot } from './app/session.js'
import { takeRoomCode } from './app/route.js'
import { takeEmailLink } from './app/emailLinkToken.js'
import { ResumeCard } from './lobby/ResumeCard.js'
import { ConnectedInviteSheet, Lobby, LobbyDock, currentNickname, useLobbyFlow } from './lobby/index.js'
import { ConnectedPassDeviceScreen } from './local/PassDeviceScreen.js'
import { leaveLocalGame, startLocalGame, submitLocalAction, useLocalSessionStore } from './local/localSession.js'
import { RoomHeader, RoomLinkProvider, RoomOverlays, RoomScreen } from './room/index.js'
import { HandEndDock, HandResultOverlay as LocalHandResultOverlay } from './session/HandResultOverlay.js'
import { Toasts } from './session/Toasts.js'
import { UpdateNotice } from './session/UpdateNotice.js'
import { useSettingsStore, useTableStore, useUiStore } from './store/index.js'
import { TableScene } from './table/TableScene.js'
import { selectNetView, useNetStore } from './transport/applyMessage.js'
import type { LocalSetup } from './transport/LocalEngineTransport.js'

// ============================================================================
// 联机：顶栏（大厅态）
// ============================================================================

function LobbyTopBar({ status }: { readonly status: string }): ReactNode {
  return (
    <>
      <div className="club-wordmark"><b translate="no">GÖDEL</b><small>德州扑克 · 私人牌室</small></div>
      {status !== 'ready' && (
        <span style={{ fontSize: 'var(--fs-badge)', color: 'var(--c-text-mute)' }}>
          {status === 'error' ? '连不上服务器' : '连接中…'}
        </span>
      )}
      {/* 常驻声明：产品定位一致性，也降低应用商店的博彩分类风险 */}
    </>
  )
}

// ============================================================================
// 本地传手机模式（次要路径）
// ============================================================================

/** `ActionIntent` → 本地引擎。`to` 恒为 raise-to 累计额（红线 #18），原样透传。 */
function submitIntent(intent: ActionIntent): void {
  switch (intent.type) {
    case 'bet':
    case 'raise':
      submitLocalAction(intent.type, intent.to)
      return
    default:
      submitLocalAction(intent.type)
  }
}

/**
 * 本地模式的 dock。结算后行动条已经没有可按的东西，那块地方让给「下一手」。
 * （联机模式不需要这个分支：服务端到点自动开下一手。）
 */
function LocalDock(): ReactNode {
  const settled = useTableStore((s) => s.view?.result != null)
  // preAction={false}：手机就在自己手里，预选毫无意义（还会和遮牌页抢同一次点击）
  return settled ? <HandEndDock /> : <ActionDock onSubmit={submitIntent} preAction={false} remainingMs={null} />
}

/** 本地模式顶栏：只有「退出」。联机的顶栏是 RoomHeader。 */
function LocalTopBar(): ReactNode {
  const handNo = useLocalSessionStore((s) => s.handNo)
  return (
    <>
      {/* 和大厅那个入口用同一句话：「本地试玩」是它被删掉的那个同义说法 */}
      <strong style={{ fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-bold)' }}>一台手机轮流玩</strong>
      {handNo > 0 && (
        <span className="num" style={{ color: 'var(--c-gold)', fontSize: 'var(--fs-sm)' }}>
          第 {handNo} 手
        </span>
      )}
      <AudioMenu />
      <button
        type="button"
        onClick={() => leaveLocalGame()}
        style={{
          marginInlineStart: 'auto',
          minHeight: 32,
          padding: '0 var(--sp-3)',
          borderRadius: 'var(--r-pill)',
          border: '1px solid var(--c-line-strong)',
          background: 'var(--c-surface-2)',
          color: 'var(--c-text-dim)',
          fontSize: 'var(--fs-badge)',
          fontWeight: 'var(--fw-bold)',
        }}
      >
        退出
      </button>
    </>
  )
}

// ============================================================================
// 路由：地址栏带码 → 自动进房
// ============================================================================

/**
 * 地址栏里的房间码（`/r/K7M2P9` 或 `/join?room=…&code=…`）→ 一次自动进房。
 *
 * 这同时是「刷新不丢当前房间」的实现：在房间里时 `startRoomRouteSync` 会把
 * `/r/CODE` 写进地址栏，刷新后这里再用同一个码 `joinRoom` 回去 ——
 * 服务端按 accountId 认人，座位、筹码、手牌都还在（断线重连那套）。
 *
 * ★ 必须等连接就绪再发：没连上时 `submitJoin` 只会得到一句「连不上服务器」的噪音。
 */
function useAutoJoin(code: string | null, ready: boolean): void {
  const fired = useRef(false)
  useEffect(() => {
    if (code === null || !ready || fired.current) return
    fired.current = true
    const flow = useLobbyFlow.getState()
    flow.setLastCode(code)
    flow.go('join')
    flow.submitJoin(code, currentNickname())
  }, [code, ready])
}

/**
 * 房间没了就别把人留在空桌上（被踢、房间被关后点了「回大厅」、离房重连）。
 * `fatal` 非空时按兵不动：那时断线条上有话要说，还有一个「回大厅」按钮。
 */
function useBounceHome(active: boolean): void {
  const netView = useNetStore(selectNetView)
  const fatal = useNetStore((s) => s.fatal)
  const setView = useUiStore((s) => s.setView)
  useEffect(() => {
    if (!active || netView !== null || fatal !== null) return
    setView('lobby')
  }, [active, netView, fatal, setView])
}

// ============================================================================
// 根
// ============================================================================

export function AppRoot(): ReactNode {
  // ★ 在**首次渲染体内**读走地址里的房间码：必须早于 Lobby 挂载，
  //   否则 Lobby 的 consumeInviteCode 会先看到它，两处各消费一次。
  /**
   * ★ 必须排在 takeRoomCode **之前**，而且同样在首次渲染体内：
   *   boot 里的 startRoomRouteSync() 会无条件把地址抹成 '/'，effect 跑的时候令牌已经没了。
   *   读出来的东西存在模块级的 pendingEmailLink() 里，由大厅的面板接手 ——
   *   这里不用它，只是抢在抹地址之前把它取走。
   */
  useState(() => takeEmailLink())
  const [inviteCode] = useState(() => takeRoomCode())
  const boot = useOnlineBoot()
  const view = useUiStore((s) => s.view)
  const localMode = useLocalSessionStore((s) => s.status !== 'idle')
  const musicEnabled = useSettingsStore((s) => s.music)

  useBackgroundMusic(view === 'table', musicEnabled)
  // iOS 静音拨杆：音效开着时让 Web Audio 走「媒体播放」，静音模式下也有声（见 setPlaybackAudioSession）
  const soundEnabled = useSettingsStore((s) => s.sound)
  useEffect(() => {
    setPlaybackAudioSession(soundEnabled)
  }, [soundEnabled])

  useAutoJoin(inviteCode, boot.status === 'ready')
  useBounceHome(view === 'table' && !localMode)

  const onStartLocal = useCallback((setup: LocalSetup) => {
    // autoNextHand=false：几个人凑着一块屏幕看结果，固定间隔翻页会把结算卡抢走
    startLocalGame({ ...setup, autoNextHand: false })
  }, [])

  const onLeaveRoom = useCallback(() => {
    leaveRoomAndReset()
  }, [])

  if (localMode) {
    return (
      <App
        topBar={<LocalTopBar />}
        lobby={<Lobby onStart={onStartLocal} />}
        lobbyDock={<LobbyDock />}
        table={<TableScene dock={<LocalDock />} stageOverlay={<LocalHandResultOverlay />} />}
        veil={<ConnectedPassDeviceScreen />}
        overlays={
          <>
            <UpdateNotice />
            <Toasts />
          </>
        }
      />
    )
  }

  const link = boot.session?.transport

  // 联机的单手结果由 RoomScreen 自己渲染。不要再注入本地模式的结算卡，
  // 否则两套组件会读到同一手牌，在桌面上重复展示一遍。
  const shell = (
    <App
      topBar={view === 'table' ? <RoomHeader onLeave={onLeaveRoom} /> : <LobbyTopBar status={boot.status} />}
      lobby={
        <>
          {/* 跨设备续局入口：换了手机/清了缓存时，不用记房间码也能回原座位 */}
          <ResumeCard enabled={boot.status === 'ready'} />
          <Lobby onStart={onStartLocal} />
        </>
      }
      lobbyDock={<LobbyDock />}
      table={<RoomScreen />}
      overlays={
        <>
          <RoomOverlays onLeave={onLeaveRoom} />
          <ConnectedInviteSheet />
          <UpdateNotice />
          <Toasts />
        </>
      }
    />
  )

  // 还没建好连接时不套 Provider：RoomLink 的默认实现是全空操作，
  // 房间组件照常渲染（不会崩），发出去的消息落空而不是崩掉一整桌。
  return link === undefined ? shell : <RoomLinkProvider link={link}>{shell}</RoomLinkProvider>
}

export default AppRoot
